import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useGetEmployeeById } from '../../../../../services/react-query/employees';
import { useGetAllEvents } from '../../../../../services/react-query/events';
import { Event } from '../../../../../types/Event';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes } from '../../../../../utility/dateUtils';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import _ from 'lodash';

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const EmployeeDrawerView: React.FC<{ uuid: string }> = ({ uuid }) => {
  const { data: employee, isLoading } = useGetEmployeeById({ uuid });

  const { data: eventsResp, isLoading: eventsLoading } = useGetAllEvents({
    params: {
      per_page: 5,
      sort: { start_at: 1 as const },
      filters: { employees: [uuid] },
    },
    enabled: !!uuid,
  });

  if (isLoading) return <CenterSpinner />;
  if (!employee) {
    return (
      <Typography variant="body2" color="error">
        Employee not found
      </Typography>
    );
  }

  const events = eventsResp?.data || [];

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(employee.display_name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Employee Information */}
          <DrawerSection title="Employee Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow label="Chosen Name" value={employee.chosen_name} />
              <InfoRow label="First Name" value={employee.first_name} />
              <InfoRow label="Last Name" value={employee.last_name} />
              <InfoRow label="Title" value={employee.title_value} />
              <InfoRow label="Roles" value={employee.roles_values?.join(', ')} />
              <InfoRow label="Email" value={employee.email} />
              <InfoRow label="Phone" value={employee.phone ? `${employee.phone}${employee.extension ? ` x${employee.extension}` : ''}` : null} />
              <InfoRow label="Code" value={employee.code} />
            </Box>
          </DrawerSection>

          {/* Events */}
          <DrawerSection title="Events">
            {eventsLoading ? (
              <CenterSpinner />
            ) : events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {events.map((event: Event) => (
                  <Box key={event.uuid} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }} title={event.display_name}>
                      {event.display_name}
                    </Typography>
                    <InfoRow label="Date / Time" value={humanizeEventDateTimes(event)} />
                    <InfoRow label="Status" value={event.status || '—'} rightNode={<StatusPill status={event.status} />} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default EmployeeDrawerView;
